import { writable, derived, get } from 'svelte/store';
import type { Writable, Readable } from 'svelte/store';
import { AuthService, type User } from '../lib/api/auth.service';
import { appData } from './user-data.store';
import { push } from 'svelte-spa-router';

// Create a writable store for the authenticated user
const userStore: Writable<User | null> = writable(AuthService.getUser());

// Derived store for authentication status
// Allow access if user exists and has a token, regardless of email verification
const isAuthenticated: Readable<boolean> = derived(
  userStore,
  $user => ($user !== null && AuthService.isAuthenticated())
);

// Derived store for email verification status
const isEmailVerified: Readable<boolean> = derived(
  userStore,
  $user => ($user !== null && $user.email_verified_at !== null)
);

// Function to update the store when auth state changes
function updateStore() {
  userStore.set(AuthService.getUser());
}

// Auth store actions
const auth = {
  // Initialize the store with current auth state
  init: () => {
    updateStore();
  },
  
  // Login action
  login: async (email: string, password: string) => {
    const result = await AuthService.login({ email, password });
    if (result.success && result.user) {
      userStore.set(result.user);
    }
    
    return result;
  },
  
  // Register action
  register: async (userData: {
    full_name: string;
    email: string;
    phone: string;
    password: string;
    password_confirmation: string;
  }) => {
    const result = await AuthService.register(userData);
    if (result.success && result.user) {
      userStore.set(result.user);
    }
    return result;
  },
  
  // Verify OTP action
  verifyOtp: async (otpCode: string) => {
    const result = await AuthService.verifyOtp({ otp_code: otpCode });
    if (result.success && result.user) {
      userStore.set(result.user);
    }
    return result;
  },

  // Verify OTP action
  verifyOtpEmail: async (otpCode: string, email: string) => {
    const result = await AuthService.verifyOtpEmail({ otp_code: otpCode, email });
    if (result.success && result.user) {
      userStore.set(result.user);
    }
    return result;
  },

  // Resend OTP action
  resendOtpEmail: async (email: string) => {
    return await AuthService.resendOtpEmail({ email });
  },
    
  
  // Resend OTP action
  resendOtp: async (email: string) => {
    return await AuthService.resendOtp({ email });
  },
  
  // Logout action
  logout: async () => {
    const result = await AuthService.logout();
    userStore.set(null);
    // Clear app data when user logs out
    appData.clearAll();

    return result;
  },
  
  // Refresh user data
  refreshUser: async () => {
    if (!AuthService.isAuthenticated()) {
      userStore.set(null);
      return { success: false };
    }
    
    const result = await AuthService.getCurrentUser();
    if (result.success && result.user) {
      userStore.set(result.user);
    }
    return result;
  },
  
  // Get the current user
  getUser: (): User | null => get(userStore)
};

export { userStore, isAuthenticated, isEmailVerified, auth }; 
